
import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Older admin navigation wrote `tab` into the origin query instead of the
// hash route. Move it into /admin and remove it everywhere else.
if (typeof window !== 'undefined') {
  const url = new URL(window.location.href);
  const legacyTab = url.searchParams.get('tab');
  if (legacyTab) {
    if (url.hash.startsWith('#/admin')) {
      const adminRoute = new URL(url.hash.slice(1), url.origin);
      if (!adminRoute.searchParams.has('tab')) {
        adminRoute.searchParams.set('tab', legacyTab);
        url.hash = `${adminRoute.pathname}${adminRoute.search}`;
      }
    }
    url.searchParams.delete('tab');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }
}

// Ensure direct URL deep paths redirect seamlessly to hash routes preventing server 500 errors
if (typeof window !== 'undefined' && !window.location.hash && window.location.pathname !== '/' && window.location.pathname !== '/index.html') {
  const currentPath = window.location.pathname + window.location.search;
  window.location.replace('/#' + currentPath);
}

const Router = HashRouter;
import BottomNav from './components/BottomNav';
// Feed stays eager: it is the landing route and must paint immediately.
// Everything else is code-split, so the first load only downloads the JS the
// visitor actually needs instead of one ~2 MB bundle containing all 20 pages.
import Feed from './pages/Feed';
const Reads = lazy(() => import('./pages/Reads'));
const Library = lazy(() => import('./pages/Library'));
const Studio = lazy(() => import('./pages/Studio'));
const Search = lazy(() => import('./pages/Search'));
const Profile = lazy(() => import('./pages/Profile'));
const Reader = lazy(() => import('./pages/Reader'));
const Auth = lazy(() => import('./pages/Auth'));
const AuthorProfile = lazy(() => import('./pages/AuthorProfile'));
const BookInfo = lazy(() => import('./pages/BookInfo'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const Notifications = lazy(() => import('./pages/Notifications'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Competition = lazy(() => import('./pages/Competition'));
const Leaderboards = lazy(() => import('./pages/Leaderboard'));
const Challenges = lazy(() => import('./pages/Challenges'));
const Insights = lazy(() => import('./pages/Insights'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Settings = lazy(() => import('./pages/Settings'));
const NotFound = lazy(() => import('./pages/NotFound'));
import { supabaseService, supabase, isRlsMisconfigured } from "./supabaseService";
import PenNameModal from './components/PenNameModal';
import AppDownloadBanner from './components/AppDownloadBanner';
import LaunchOverlay from './components/LaunchOverlay';
import LiterallyLoader from './components/LiterallyLoader';
import AdBlockerModal from './components/AdBlockerModal';
import { Profile as ProfileType } from './types';
import { trackAdActivity } from './lib/adTracker';
import {
  applySettingsToDocument,
  resolveTheme,
  useSettings,
} from './lib/settings';

import { HelmetProvider } from 'react-helmet-async';

// Error Boundary Component
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: any }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-slate-50 dark:bg-slate-950 text-center">
          <div className="w-20 h-20 bg-rose-100 dark:bg-rose-900/20 rounded-full flex items-center justify-center mb-6">
            <span className="text-4xl text-rose-500">âš ï¸</span>
          </div>
          <h1 className="text-2xl font-black italic mb-2 dark:text-white">Something went wrong.</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-md">
            The application encountered an unexpected error. Don't worry, your ink is safe.
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-8 py-4 bg-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-indigo-200 dark:shadow-none"
          >
            Reload Application
          </button>
          {(true) && (
            <pre className="mt-8 p-4 bg-slate-100 dark:bg-slate-900 rounded-xl text-xs text-left overflow-auto max-w-full text-rose-500">
              {this.state.error?.toString()}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
      className="h-full"
    >
      {children}
    </motion.div>
  );
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check synchronously for any Supabase auth token keys in localStorage
    try {
      return typeof window !== 'undefined' && Object.keys(localStorage).some(key => key.includes('auth-token'));
    } catch {
      return false;
    }
  });
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const { settings, update: updateSetting } = useSettings();
  const [isAppReady, setIsAppReady] = useState(false);
  const [initError, setInitError] = useState(false);
  const [isAIConfigured, setIsAIConfigured] = useState(true);

  const initApp = async () => {
      console.log("initApp: Starting parallel initialization...");
      setInitError(false);
      
      try {
          // Parallelize profile, session, and AI status, plus a smooth minimum presentation duration
          const minDelay = new Promise((resolve) => setTimeout(resolve, 500));

          const profilePromise = supabaseService.getProfileWithRetry().catch((err) => {
              console.warn("initApp: Failed to load profile, using fallback null:", err);
              return null;
          });
          
          const getSessionTimeout = (promise: Promise<any>, ms: number) => {
              return new Promise((resolve, reject) => {
                  const timer = setTimeout(() => reject(new Error("Timeout")), ms);
                  promise.then((res) => {
                      clearTimeout(timer);
                      resolve(res);
                  }).catch((err) => {
                      clearTimeout(timer);
                      reject(err);
                  });
              });
          };

          const sessionPromise = supabase ? getSessionTimeout(supabase.auth.getSession(), 4000) : Promise.resolve({ data: { session: null } });
          
          const aiStatusPromise = fetch('/api/ai-status', { signal: AbortSignal.timeout ? AbortSignal.timeout(2500) : undefined })
            .then(r => r.json())
            .catch(() => ({ configured: true }));

          const [userResult, sessionResult, aiStatus] = await Promise.all([
              profilePromise,
              sessionPromise.catch(() => ({ data: { session: null } })),
              aiStatusPromise,
              minDelay
          ]);

          // Process results
          const user = userResult as ProfileType | null;
          const session = (sessionResult as any).data?.session;

          if (session?.user) {
              const isGoogle = session.user.app_metadata?.provider === 'google' || 
                               session.user.identities?.some((id: any) => id.provider === 'google') ||
                               sessionStorage.getItem('literally_auth_provider') === 'google';
              if (isGoogle) {
                  localStorage.setItem(`literally_is_google_user_${session.user.id}`, 'true');
              } else {
                  localStorage.setItem(`literally_is_google_user_${session.user.id}`, 'false');
                  localStorage.setItem(`literally_pen_name_claimed_${session.user.id}`, 'true');
              }
          }

          if (user && user.is_active !== false) {
              console.log("initApp: Profile loaded");
              setIsAuthenticated(true);
              setProfile(user);
              supabaseService.ensureGlobalCommunity();
              trackAdActivity({ eventType: 'session' });
          } else if (session) {
              console.log("initApp: Session active but no profile. Attempting fallback generation if valid...");
              const spawned = await supabaseService.autoCreateProfile(session.user);
              if (spawned && spawned.is_active !== false) {
                  setProfile(spawned);
                  setIsAuthenticated(true);
                  trackAdActivity({ eventType: 'session' });
              } else if (spawned?.is_active === false) {
                  console.log("initApp: Profile is deactivated, clearing session.");
                  setIsAuthenticated(false);
                  setProfile(null);
                  await supabaseService.signOut().catch(() => {});
              } else {
                  console.log("initApp: Profile creation failed (network/db error), maintaining session but showing limited state.");
                  setProfile({
                      id: session.user.id,
                      email: session.user.email || "mock@example.com",
                      full_name: "Local User",
                      display_name: "Local User",
                      bio: "Offline mode.",
                      joined_date: new Date().toISOString(),
                      reputation: 0,
                      is_author: false,
                      is_admin: false,
                      is_active: true,
                      preferences: { theme: 'system', font_size: 'medium', notifications: true }
                  } as any);
                  setIsAuthenticated(true);
              }
          } else {
              console.log("initApp: No session and no profile found.");
              setIsAuthenticated(false);
              setProfile(null);
          }

          setIsAIConfigured(!!aiStatus?.configured);
          setIsAppReady(true);
      } catch (err) {
          console.error("App initialization failed (network or timeout):", err);
          setInitError(true);
      }
  };

  useEffect(() => {
    // Smoothly fade out the HTML splash screen once the React app is fully initialized
    if (isAppReady) {
      const splash = document.getElementById('literally-splash-screen') || document.getElementById('app-initial-loader') || document.getElementById('splash-screen');
      if (splash) {
        splash.style.transition = 'opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1), transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)';
        splash.style.opacity = '0';
        splash.style.transform = 'scale(1.02)';
        const timer = setTimeout(() => {
          splash.remove();
        }, 360);
        return () => clearTimeout(timer);
      }
    }
  }, [isAppReady]);

  useEffect(() => {
    initApp();

    let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
    let isReloadingForUpdate = false;
    let hasSeenController = 'serviceWorker' in navigator && Boolean(navigator.serviceWorker.controller);
    const handleControllerChange = () => {
        if (hasSeenController && !isReloadingForUpdate) {
            isReloadingForUpdate = true;
            window.location.reload();
        }
        hasSeenController = true;
    };
    const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && serviceWorkerRegistration) {
            serviceWorkerRegistration.update().catch((err) => {
                console.warn('ServiceWorker update check failed: ', err);
            });
        }
    };

    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).then((registration) => {
            serviceWorkerRegistration = registration;
            registration.update().catch((err) => {
                console.warn('ServiceWorker update check failed: ', err);
            });
            console.log('ServiceWorker registration successful with scope: ', registration.scope);
            if ('Notification' in window && window.Notification.permission === 'default') {
                window.Notification.requestPermission();
            }
        }).catch((err) => {
            console.warn('ServiceWorker registration failed: ', err);
        });
    }

    // Listen for auth state changes (crucial for OAuth redirects)
    const { data: { subscription } } = supabaseService.onAuthStateChange(async (event, session) => {
        try {
            console.log("Auth event:", event);
            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
                if (session?.user) {
                    const isGoogle = session.user.app_metadata?.provider === 'google' || 
                                     session.user.identities?.some((id: any) => id.provider === 'google') ||
                                     sessionStorage.getItem('literally_auth_provider') === 'google';
                    if (isGoogle) {
                        localStorage.setItem(`literally_is_google_user_${session.user.id}`, 'true');
                    } else {
                        localStorage.setItem(`literally_is_google_user_${session.user.id}`, 'false');
                        localStorage.setItem(`literally_pen_name_claimed_${session.user.id}`, 'true');
                    }

                    let userProfile = await supabaseService.getProfileWithRetry(session.user.id);
                    if (!userProfile) {
                        console.log("Auth listener: Profile missing. Creating proactively...");
                        userProfile = await supabaseService.autoCreateProfile(session.user).catch(() => null);
                    }
                    if (userProfile && userProfile.is_active !== false) {
                        setProfile(userProfile);
                        setIsAuthenticated(true);
                    } else if (userProfile?.is_active === false) {
                        console.log("Auth listener: Profile is deactivated. Signing out...");
                        setIsAuthenticated(false);
                        setProfile(null);
                        await supabaseService.signOut().catch(() => {});
                    } else {
                        console.log("Auth listener: Profile creation failed (network/db error), maintaining session but showing limited state.");
                        setProfile({
                            id: session.user.id,
                            email: session.user.email || "mock@example.com",
                            full_name: "Local User",
                            display_name: "Local User",
                            bio: "Offline mode.",
                            joined_date: new Date().toISOString(),
                            reputation: 0,
                            is_author: false,
                            is_admin: false,
                            is_active: true,
                            preferences: { theme: 'system', font_size: 'medium', notifications: true }
                        } as any);
                        setIsAuthenticated(true);
                    }
                } else {
                    setIsAuthenticated(false);
                    setProfile(null);
                }
            } else if (event === 'SIGNED_OUT') {
                setIsAuthenticated(false);
                setProfile(null);
            }
        } catch (err) {
            console.error("Auth listener error:", err);
        }
    });

    const handleProfileUpdate = (e: any) => {
        if (e.detail) setProfile(e.detail);
    };
    window.addEventListener('profileUpdated', handleProfileUpdate);
    
    // Global active push notifications if SW is running
    const unsubscribeNotes = supabaseService.subscribeToNotifications((newNote: any) => {
        if ('Notification' in window && window.Notification.permission === 'granted' && document.visibilityState !== 'visible') {
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.ready.then(registration => {
                    registration.showNotification('Literally', {
                        body: newNote.preview_text,
                        icon: '/icon.png',
                        badge: '/icon.png',
                        data: '/'
                    });
                }).catch(err => console.error("SW not ready", err));
            }
        }
    });

    return () => {
        subscription.unsubscribe();
        window.removeEventListener('profileUpdated', handleProfileUpdate);
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (typeof unsubscribeNotes === 'function') unsubscribeNotes();
    };
  }, []);

  // Applies every layout-affecting preference to the document in one place:
  // theme class, accent variable, reader typography and reduced motion.
  useEffect(() => {
    applySettingsToDocument(settings);
    setTheme(resolveTheme(settings.theme));
  }, [settings]);

  // Follows the OS when the user has chosen "system".
  useEffect(() => {
    if (settings.theme !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setTheme(resolveTheme('system'));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [settings.theme]);

  // Content Protection: Block right-clicks and common copy/screenshot shortcuts on protected content
  useEffect(() => {
    const isTargetEditable = (target: EventTarget | null) => {
      if (!target) return false;
      const el = target as HTMLElement;
      return (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el.isContentEditable ||
        Boolean(el.closest?.('[contenteditable="true"]')) ||
        Boolean(el.closest?.('input')) ||
        Boolean(el.closest?.('textarea'))
      );
    };

    const handleContextMenu = (e: MouseEvent) => {
      // Allow right clicks on text inputs/editors, but protect reading content
      if (!isTargetEditable(e.target)) {
        e.preventDefault();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // If typing or editing inside Studio/inputs, allow all shortcuts
      if (isTargetEditable(e.target)) {
        return;
      }

      const isCmdOrCtrl = e.metaKey || e.ctrlKey;
      // Block Ctrl+S (Save), Ctrl+P (Print), Ctrl+U (Source), Ctrl+C (Copy), F12 (DevTools)
      if (
        (isCmdOrCtrl && ['s', 'p', 'u', 'c'].includes(e.key.toLowerCase())) ||
        ['F12', 'PrintScreen'].includes(e.key)
      ) {
        e.preventDefault();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'PrintScreen') {
        try { navigator.clipboard.writeText(""); } catch (err) {}
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const toggleTheme = () => {
    updateSetting('theme', settings.theme === 'dark' ? 'light' : 'dark');
  };

  const handleLogout = async () => {
    try {
        await supabaseService.signOut();
        setIsAuthenticated(false);
        setProfile(null);
    } catch (err) {
        console.error("Logout failed:", err);
    }
  };

  if (!isAppReady || initError) {
    return (
      <LiterallyLoader 
        hasError={initError}
        onRetry={() => {
          setInitError(false);
          setIsAppReady(false);
          initApp();
        }}
        errorMsg="Connecting to Literally... Please check your internet connection."
      />
    );
  }



  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <AppContent
            isAuthenticated={isAuthenticated}
            setIsAuthenticated={setIsAuthenticated}
            profile={profile}
            setProfile={setProfile}
            theme={theme}
            toggleTheme={toggleTheme}
            handleLogout={handleLogout}
          />
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

const SharedLinkRedirect: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    // 1. Check search query parameters (e.g. ?post=..., ?poem=..., ?article=..., ?book=..., ?read=...)
    const params = new URLSearchParams(window.location.search);
    const hashQuery = window.location.hash.includes('?') 
      ? new URLSearchParams(window.location.hash.split('?')[1]) 
      : null;

    const postId = params.get('post') || params.get('article') || params.get('essay') || hashQuery?.get('post') || hashQuery?.get('article') || hashQuery?.get('essay');
    const bookId = params.get('book') || hashQuery?.get('book');
    const readBookId = params.get('read') || hashQuery?.get('read');
    const poemId = params.get('poem') || hashQuery?.get('poem');

    if (postId) {
      navigate(`/post/${postId}`, { replace: true });
      return;
    }

    // Poems should go to the poems/feed page with the poem loaded so readers can scroll other works
    if (poemId) {
      navigate(`/poem/${poemId}`, { replace: true });
      return;
    }

    if (bookId || readBookId) {
      // Books redirect to the main book page
      navigate(`/book/${bookId || readBookId}`, { replace: true });
      return;
    }

  }, [navigate]);
  return null;
};

/**
 * Dynamic SEO Management System
 * Keeps canonical URLs on the clean paths served by prerendering and listed
 * in the sitemap, while the app continues to use its client-side router.
 */
const DynamicCanonicalSEO: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const baseUrl = 'https://www.literally.com.ng';
    const path = location.pathname;
    const search = location.search || '';
    const uuid = path.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0];
    const contentPath = path.match(/^\/(?:post|poem|article|essay)\/[^/]+$/i)
      ? `/post/${uuid || path.split('/').pop()}`
      : path.match(/^\/book\/[^/]+$/i) && uuid
        ? `/book/${uuid}`
        : path;
    const canonicalUrl = `${baseUrl}${contentPath}${search}`;

    // Inject or update <link rel="canonical"> in document.head
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);

    // Synchronize og:url and twitter:url to point to the canonical URL structure
    let ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', canonicalUrl);

    let twitterUrl = document.querySelector('meta[name="twitter:url"]') as HTMLMetaElement | null;
    if (!twitterUrl) {
      twitterUrl = document.createElement('meta');
      twitterUrl.setAttribute('name', 'twitter:url');
      document.head.appendChild(twitterUrl);
    }
    twitterUrl.setAttribute('content', canonicalUrl);
  }, [location.pathname, location.search]);

  return null;
};

interface AppContentProps {
  isAuthenticated: boolean;
  setIsAuthenticated: (val: boolean) => void;
  profile: any;
  setProfile: (profile: any) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  handleLogout: () => void;
}

const AppContent: React.FC<AppContentProps> = ({
  isAuthenticated,
  setIsAuthenticated,
  profile,
  setProfile,
  theme,
  toggleTheme,
  handleLogout,
}) => {
  const handleLogin = useCallback(() => setIsAuthenticated(true), [setIsAuthenticated]);

  const location = useLocation();
  const navigate = useNavigate();

  // Redirect to auth when an unauthenticated action is attempted (e.g. liking, commenting)
  useEffect(() => {
    const handleAuthRequired = () => {
      navigate('/auth');
    };
    window.addEventListener('auth-required', handleAuthRequired);
    return () => window.removeEventListener('auth-required', handleAuthRequired);
  }, [navigate]);

  // Track Google Analytics SPA pageviews
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('config', 'G-746J6W36EE', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location.pathname, location.search]);

  const isSharedLink = location.pathname.startsWith('/book/') || 
                       location.pathname.startsWith('/post/') || 
                       location.pathname.startsWith('/author/') ||
                       location.pathname.startsWith('/terms') ||
                       location.pathname.startsWith('/privacy');

  const isAdminPage = location.pathname.startsWith('/admin');

  return (
    <div className={`min-h-screen ${isAdminPage ? 'pb-0 md:pb-0 md:pl-0' : isAuthenticated ? 'pb-20 md:pb-0 md:pl-60' : 'pb-20 md:pb-0 md:pl-60'} dark:bg-slate-950 transition-colors duration-300 relative`}>
      <DynamicCanonicalSEO />
      <SharedLinkRedirect />
      {/* Ambient Background for Desktop */}
      {!isAdminPage && (
        <div className="hidden md:block ambient-bg">
          <div className="ambient-blob bg-indigo-500 -top-[10%] -left-[10%]" />
          <div className="ambient-blob bg-purple-500 -bottom-[10%] -right-[10%] animation-delay-2000" />
          <div className="ambient-blob bg-blue-500 top-[20%] right-[10%] animation-delay-4000" />
        </div>
      )}

      {isAuthenticated && profile && (() => {
        // ONLY prompt user to claim their pen name if they signed in with Google.
        // Users that registered using their email already chose theirs so there is no need to ask again.
        const isGoogleUser = 
          profile.auth_provider === 'google' ||
          localStorage.getItem(`literally_is_google_user_${profile.id}`) === 'true' ||
          sessionStorage.getItem('literally_auth_provider') === 'google';

        if (!isGoogleUser) return false;

        const isDismissed = localStorage.getItem(`literally_pen_name_dismissed_${profile.id}`) === 'true' ||
                            localStorage.getItem(`literally_pen_name_claimed_${profile.id}`) === 'true';
        if (isDismissed) return false;
        
        const isDefaultName = !profile.display_name || profile.display_name.startsWith('User_');
        return profile.has_selected_pen_name === false || isDefaultName;
      })() && (
        <PenNameModal 
          profile={profile} 
          onComplete={(updated) => { 
            if (updated) setProfile(updated); 
            localStorage.setItem(`literally_pen_name_claimed_${profile.id}`, 'true');
          }} 
          onDismiss={() => {
            if (profile?.id) {
              localStorage.setItem(`literally_pen_name_dismissed_${profile.id}`, 'true');
            }
            setProfile({ ...profile, has_selected_pen_name: true });
          }}
        />
      )}
      
      {!isAdminPage && <AppDownloadBanner />}
      
      <div className={isAdminPage ? "w-full min-h-screen" : "md:p-4 md:h-screen md:overflow-hidden"}>
          <div className={isAdminPage ? "w-full min-h-screen" : "md:h-full md:overflow-y-auto md:rounded-[40px] md:bg-white/40 md:dark:bg-slate-900/40 md:backdrop-blur-xl md:border md:border-white/20 md:dark:border-slate-800/50 md:shadow-2xl"}>
            <AnimatePresence mode="wait">
              {/* Route chunks load on demand; this is the brief placeholder that
                  covers the gap so navigation never flashes an empty screen. */}
              <Suspense
                fallback={
                  <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="w-10 h-10 rounded-2xl bg-[#5F4BFF] animate-pulse shadow-lg shadow-[#5F4BFF]/25" />
                  </div>
                }
              >
              <Routes location={location} key={location.pathname}>
                <Route path="/auth" element={isAuthenticated ? <Navigate to="/" replace /> : <Auth onLogin={handleLogin} />} />
                <Route path="/" element={<PageWrapper><Feed /></PageWrapper>} />
                <Route path="/about" element={<PageWrapper><LandingPage /></PageWrapper>} />
                <Route path="/feed" element={<PageWrapper><Feed /></PageWrapper>} />
                <Route path="/poems" element={<PageWrapper><Feed /></PageWrapper>} />
                <Route path="/poems/:slug" element={<PageWrapper><Feed /></PageWrapper>} />
                <Route path="/reads" element={<PageWrapper><Reads /></PageWrapper>} />
                <Route path="/library" element={<PageWrapper><Library /></PageWrapper>} />
                <Route path="/studio" element={isAuthenticated ? <PageWrapper><Studio /></PageWrapper> : <Navigate to="/auth" />} />
                <Route path="/insights" element={isAuthenticated ? <PageWrapper><Insights /></PageWrapper> : <Navigate to="/auth" />} />
                <Route path="/search" element={<PageWrapper><Search /></PageWrapper>} />
                <Route path="/competition" element={<PageWrapper><Competition /></PageWrapper>} />
                <Route path="/competitions" element={<PageWrapper><Competition /></PageWrapper>} />
                <Route path="/leaderboards" element={<PageWrapper><Leaderboards /></PageWrapper>} />
                <Route path="/challenges" element={<PageWrapper><Challenges /></PageWrapper>} />
                <Route 
                  path="/profile" 
                  element={isAuthenticated ? <PageWrapper><Profile onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} /></PageWrapper> : <Navigate to="/auth" />} 
                />
                <Route path="/book/:bookId" element={<PageWrapper><BookInfo /></PageWrapper>} />
                <Route path="/book/:author/:bookId" element={<PageWrapper><BookInfo /></PageWrapper>} />
                <Route path="/book/:title/:author/:bookId" element={<PageWrapper><BookInfo /></PageWrapper>} />

                <Route path="/post/:postId" element={<PageWrapper><PostDetail /></PageWrapper>} />
                <Route path="/post/:author/:postId" element={<PageWrapper><PostDetail /></PageWrapper>} />
                <Route path="/post/:title/:author/:postId" element={<PageWrapper><PostDetail /></PageWrapper>} />

                <Route path="/poem/:slug" element={<PageWrapper><Feed /></PageWrapper>} />
                <Route path="/poem/:author/:slug" element={<PageWrapper><Feed /></PageWrapper>} />
                <Route path="/poem/:title/:author/:slug" element={<PageWrapper><Feed /></PageWrapper>} />
                <Route path="/poems/:slug" element={<PageWrapper><Feed /></PageWrapper>} />

                <Route path="/article/:postId" element={<PageWrapper><PostDetail /></PageWrapper>} />
                <Route path="/article/:author/:postId" element={<PageWrapper><PostDetail /></PageWrapper>} />
                <Route path="/article/:title/:author/:postId" element={<PageWrapper><PostDetail /></PageWrapper>} />

                <Route path="/essay/:postId" element={<PageWrapper><PostDetail /></PageWrapper>} />
                <Route path="/essay/:author/:postId" element={<PageWrapper><PostDetail /></PageWrapper>} />
                <Route path="/essay/:title/:author/:postId" element={<PageWrapper><PostDetail /></PageWrapper>} />
                <Route path="/notifications" element={isAuthenticated ? <PageWrapper><Notifications /></PageWrapper> : <Navigate to="/auth" />} />
                <Route path="/read/:bookId" element={<PageWrapper><Reader /></PageWrapper>} />
                <Route path="/read/:author/:bookId" element={<PageWrapper><Reader /></PageWrapper>} />
                <Route path="/author/:id" element={<PageWrapper><AuthorProfile /></PageWrapper>} />
                <Route path="/admin" element={<PageWrapper><AdminDashboard /></PageWrapper>} />
                <Route path="/settings" element={<PageWrapper><Settings /></PageWrapper>} />
                
                <Route path="/terms" element={<PageWrapper><TermsPage /></PageWrapper>} />
                <Route path="/terms-of-service" element={<PageWrapper><TermsPage /></PageWrapper>} />
                <Route path="/privacy" element={<PageWrapper><PrivacyPage /></PageWrapper>} />
                <Route path="/privacy-policy" element={<PageWrapper><PrivacyPage /></PageWrapper>} />
                
                <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
              </Routes>
              </Suspense>
            </AnimatePresence>
          </div>
      </div>
      
      <BottomNav />
      <AdBlockerModal />
      <Analytics />
    </div>
  );
};

export default App;
